import { appStorage } from '../lib/safeStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarOff, X, ChevronRight } from 'lucide-react';
import { notificationsAPI } from '../services/api';

/**
 * BlockoutAlertBanner — persistent admin alert for staff unavailability.
 *
 * When a staffer submits a block-out, the backend writes a `staff_blockout`
 * notification (link: /staff/availability, metadata: staffName/startDate/
 * endDate/reason). This component surfaces every UNREAD one as a sticky card
 * that NEVER auto-dismisses — it stays until the admin acts:
 *   • Click the card  → go to Staff Availability for review + mark read
 *   • Click ✕         → dismiss (mark read) without navigating
 * Because "dismissed" === "marked read" on the server, the alert does not
 * reappear after a refresh once handled. Admin-only; renders nothing for staff.
 */

const fmtMMDDYYYY = (s) => {
  if (!s) return '';
  const p = String(s).split('-');
  return p.length === 3 ? `${p[1]}/${p[2]}/${p[0]}` : String(s);
};

const getRole = () => {
  try { return (JSON.parse(appStorage.getItem('user') || '{}').role) || ''; }
  catch { return ''; }
};

const BlockoutAlertBanner = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  // Ids the admin just acted on — kept so a poll mid-flight can't re-add them
  // before the server read-state catches up.
  const handledRef = useRef(new Set());

  const isAdmin = ['admin', 'manager'].includes(getRole());

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await notificationsAPI.getAll(20, true, 'staff_blockout');
      const items = data?.notifications || (Array.isArray(data) ? data : []);
      setAlerts(items.filter((n) => !handledRef.current.has(n.id || n._id)));
    } catch {
      /* silent — transient poll failures shouldn't disrupt the dashboard */
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    fetchAlerts();
    const id = setInterval(fetchAlerts, 30000);
    return () => clearInterval(id);
  }, [isAdmin, fetchAlerts]);

  const handle = useCallback(async (n, review) => {
    const id = n.id || n._id;
    handledRef.current.add(id);
    setAlerts((prev) => prev.filter((a) => (a.id || a._id) !== id));
    try { await notificationsAPI.markAsRead(id); } catch { /* best effort */ }
    if (review) navigate(n.link || '/staff/availability');
  }, [navigate]);

  if (!isAdmin || alerts.length === 0) return null;

  const shown = alerts.slice(0, 4);
  const extra = alerts.length - shown.length;

  return (
    <div
      className="fixed z-[9998] bottom-4 right-4 left-4 sm:left-auto sm:w-[380px] flex flex-col gap-2"
      data-testid="blockout-alert-stack"
    >
      {shown.map((n) => {
        const md = n.metadata || {};
        const dateText = md.startDate
          ? (md.startDate === md.endDate
              ? fmtMMDDYYYY(md.startDate)
              : `${fmtMMDDYYYY(md.startDate)} – ${fmtMMDDYYYY(md.endDate)}`)
          : '';
        return (
          <div
            key={n.id || n._id}
            onClick={() => handle(n, true)}
            role="alert"
            data-testid="blockout-alert"
            className="group cursor-pointer rounded-2xl border border-red-500/30 bg-[#140a0c]/95 backdrop-blur-xl p-4 shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:border-red-500/50 transition-all animate-in fade-in slide-in-from-bottom-2 duration-300"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-red-500/15 flex items-center justify-center flex-shrink-0">
                <CalendarOff className="w-4 h-4 text-red-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-[0.18em] text-red-400/80 font-semibold">
                  Staff Unavailability
                </p>
                <p className="text-sm text-white font-medium mt-0.5">
                  {(md.staffName || 'A staffer')}{' '}
                  <span className="text-gray-300">blocked out{dateText ? ` ${dateText}` : ''}</span>
                </p>
                {md.reason ? (
                  <p className="text-xs text-gray-400 mt-0.5 truncate">{md.reason}</p>
                ) : null}
                <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-[#E6C86A] group-hover:text-[#F0D77F] transition-colors">
                  Review on Staff Availability
                  <ChevronRight className="w-3 h-3" />
                </span>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handle(n, false); }}
                className="flex-shrink-0 p-1 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Dismiss alert"
                data-testid="blockout-alert-dismiss"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          </div>
        );
      })}
      {extra > 0 && (
        <button
          onClick={() => navigate('/staff/availability')}
          className="self-end text-xs text-gray-300 hover:text-white px-3 py-1.5 rounded-lg bg-black/50 border border-white/10 backdrop-blur-xl"
          data-testid="blockout-alert-more"
        >
          +{extra} more — view all
        </button>
      )}
    </div>
  );
};

export default BlockoutAlertBanner;
